import { JsonData, FetchOptions, ValidationError } from '../types';

export class JsonFetcher {
  private static instance: JsonFetcher;
  private cache = new Map<string, { data: JsonData; timestamp: number }>();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  static getInstance(): JsonFetcher {
    if (!JsonFetcher.instance) {
      JsonFetcher.instance = new JsonFetcher();
    }
    return JsonFetcher.instance;
  }

  async fetchJson(url: string, options: FetchOptions = {}): Promise<JsonData> {
    // Check cache first
    const cacheKey = `${url}-${JSON.stringify(options)}`;
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data;
    }

    // Validate URL
    if (!this.isValidUrl(url)) {
      throw new Error('Invalid URL format');
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), options.timeout || 10000);

      const response = await fetch(url, {
        method: options.method || 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...options.headers,
        },
        body: options.body ? JSON.stringify(options.body) : undefined,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        throw new Error('Response is not JSON');
      }

      const text = await response.text();
      
      // Security: Sanitize JSON to prevent XSS
      const sanitizedText = this.sanitizeJson(text);
      
      let data: JsonData;
      try {
        data = JSON.parse(sanitizedText);
      } catch (parseError) {
        throw new Error('Invalid JSON format');
      }

      // Validate JSON structure
      this.validateJson(data);

      // Cache the result
      this.cache.set(cacheKey, { data, timestamp: Date.now() });

      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to fetch JSON data');
    }
  }

  private isValidUrl(url: string): boolean {
    try {
      const urlObj = new URL(url);
      return ['http:', 'https:'].includes(urlObj.protocol);
    } catch {
      return false;
    }
  }

  private sanitizeJson(jsonString: string): string {
    // Remove potentially dangerous HTML/JS content
    return jsonString
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/on\w+\s*=/gi, '');
  }

  private validateJson(data: JsonData): void {
    const errors: ValidationError[] = [];
    
    this.validateValue(data, '', errors);
    
    if (errors.length > 0) {
      throw new Error(`Validation errors: ${errors.map(e => e.message).join(', ')}`);
    }
  }

  private validateValue(value: any, path: string, errors: ValidationError[]): void {
    if (value === null || value === undefined) return;

    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        value.forEach((item, index) => {
          this.validateValue(item, `${path}[${index}]`, errors);
        });
      } else {
        Object.keys(value).forEach(key => {
          // Check for potentially dangerous keys
          if (key.includes('__proto__') || key.includes('constructor') || key.includes('prototype')) {
            errors.push({
              path: `${path}.${key}`,
              message: 'Potentially dangerous property name',
              value: key
            });
          }
          this.validateValue(value[key], `${path}.${key}`, errors);
        });
      }
    } else if (typeof value === 'string') {
      // Check for script patterns in string values
      if (/<script|javascript:|on\w+=/i.test(value)) {
        errors.push({
          path,
          message: 'Potentially dangerous script content',
          value
        });
      }
    }
  }

  clearCache(): void {
    this.cache.clear();
  }

  getCacheSize(): number {
    return this.cache.size;
  }
}
